import { useEffect, useRef, useState } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import Link from 'next/link';
import throttle from 'lodash/throttle';
import Heading from '@components/Heading/Heading';
import RenderMedia from '@utils/RenderMedia';
import styles from '@features/cms/components/categories/CategoryCollectionCarouselBlock/CategoryCollectionCarouselBlock.styles';
import 'keen-slider/keen-slider.min.css';
import Icon from '@components/icons/Icon';
import theme from '@styles/theme';
import SlideControl from '@components/SlideControl/SlideControl';
import { ICategoryCollectionCarouselBlock, ICategoryCollectionSlides, ISlideType } from '@features/cms/typescript';
import sanitizeHtml from '@utils/sanitizeHtml';
import prepareURLByCMSLink from '@utils/prepareURLByCMSLink';
type PropsType = {
  data: ICategoryCollectionCarouselBlock;
  defaultBackground?: string;
  isFirst?: boolean;
};
const CategoryCollectionCarouselBlock = ({
  data,
  defaultBackground = theme.rawColors.neutralWhite,
  isFirst = false
}: PropsType) => {
  const {
    background,
    marginTop,
    marginBottom,
    title,
    description,
    slides: slidesData,
    titleColor,
    staticDescription,
    descriptionColor,
    code
  } = data;
  const slides = slidesData;
  const scrollContainer = useRef<HTMLDivElement>(null);
  const arrows = useRef(false);
  const [activeType, setActiveType] = useState({
    id: slides[0].slideType.id,
    description: slides[0].slideType.description
  });
  const [slideTypes, setSlideTypes] = useState<ISlideType[]>([]);
  const [activeSlides, setActiveSlides] = useState<ICategoryCollectionSlides[]>(slides);
  const [shouldRenderArrows, setShouldRenderArrows] = useState({
    left: false,
    right: true
  });
  const updateRenderArrows = slide => {
    setShouldRenderArrows({
      left: slide.track.details.rel > 0,
      right: slide.track.details.rel < slide.track.details.maxIdx
    });
  };
  const [ref, slider] = useKeenSlider<HTMLDivElement>({
    mode: 'free',
    slides: {
      perView: 'auto',
      origin: 'auto',
      spacing: 2
    },
    breakpoints: {
      '(min-width: 768px)': {
        slides: {
          perView: 'auto',
          origin: 'auto',
          spacing: 4
        }
      },
      '(min-width: 1025px)': {
        slides: {
          perView: 'auto',
          origin: 'auto',
          spacing: 24
        }
      }
    },
    slideChanged: slide => {
      updateRenderArrows(slide);
    }
  }, [slider1 => {
    let drag = false;
    function updateSize() {
      if (scrollContainer.current) {
        scrollContainer.current.style.width = `${slider1.size * slider1.track.details.slidesLength - 24}px`;
      }
    }
    slider1.on('created', () => {
      setTimeout(() => {
        if (scrollContainer.current && scrollContainer.current.parentElement) {
          scrollContainer.current.parentElement.addEventListener('scroll', () => {
            if (drag) return;
            if (scrollContainer.current && scrollContainer.current.parentElement) slider1.track.to(scrollContainer.current.parentElement.scrollLeft / (scrollContainer.current.offsetWidth - scrollContainer.current.parentElement.offsetWidth) * slider1.track.details.length);
          });
        }
        updateSize();
      });
    });
    slider1.on('updated', () => {
      updateSize();
    });
    slider1.on('dragStarted', () => {
      drag = true;
    });
    slider1.on('animationEnded', () => {
      drag = false;
      arrows.current = false;
    });
    slider1.on('detailsChanged', throttle(() => {
      if (!scrollContainer.current || !drag && !arrows.current) {
        return;
      }
      if (scrollContainer.current?.parentElement) {
        scrollContainer.current.parentElement.scrollLeft = slider1.track.details.position * slider1.size;
      }
    }, 30));
  }]);
  const [navRef, navSlider] = useKeenSlider<HTMLDivElement>({
    drag: true,
    mode: 'free',
    slides: {
      perView: 'auto',
      origin: 'auto',
      spacing: 14
    }
  });
  useEffect(() => {
    if (activeType.id) return setActiveSlides(slides.filter(slide => slide.slideType.id === activeType.id));
    return setActiveSlides(slides.filter(slide => slide.slideType.id === activeType.id));
  }, [activeType]);
  const restartMainSlider = () => {
    if (!slider.current) return;
    slider.current.update({
      mode: 'free',
      slides: {
        perView: 'auto',
        origin: 'auto',
        spacing: 2
      },
      breakpoints: {
        '(min-width: 768px)': {
          slides: {
            perView: 'auto',
            origin: 'auto',
            spacing: 4
          }
        },
        '(min-width: 1025px)': {
          slides: {
            perView: 'auto',
            origin: 'auto',
            spacing: 24
          }
        }
      },
      slideChanged: slide => {
        updateRenderArrows(slide);
      }
    });
    slider.current.moveToIdx(0, false, {
      duration: 1000
    });
    updateRenderArrows(slider.current);
  };
  useEffect(() => {
    if (!navSlider.current) return;
    navSlider.current.update({
      drag: true,
      mode: 'free',
      slides: {
        perView: 'auto',
        origin: 'auto',
        spacing: 14
      }
    });
  }, []);
  useEffect(() => {
    restartMainSlider();
  }, [activeSlides]);
  useEffect(() => {
    if (!slider.current) return;
    updateRenderArrows(slider.current);
  }, [slideTypes.length]);
  useEffect(() => {
    const st: ISlideType[] = [];
    slides.map(item => item.slideType).forEach(sliderType => {
      if (st.filter(type => type.name === sliderType.name).length === 0) st.push(sliderType);
    });
    setSlideTypes(st);
  }, []);
  return <section id={code} sx={styles.collectionSection(background || defaultBackground, marginTop, marginBottom)} data-testid={code}>
      <div sx={styles.container}>
        <Heading h={isFirst ? 1 : 2} headingType={2} mobile color={titleColor || theme.rawColors.neutralBlack} dangerouslySetInnerHTML={sanitizeHtml(title)} data-testid="category-collection-carousel-block-title" />
        <p sx={styles.styledDescription(descriptionColor || theme.rawColors.neutralBlack)} dangerouslySetInnerHTML={sanitizeHtml(staticDescription ? description : activeType.description || description)} data-testid="category-collection-carousel-block-description" />

        {slideTypes.length > 1 && <div sx={styles.tagsWrapper} data-testid="category-collection-carousel-block-tags-wrapper">
            <div sx={styles.tagsContainer} className="keen-slider" ref={navRef} data-testid="category-collection-carousel-block-tags-container">
              {slideTypes.map((slideType, index) => <button type="button" key={`${slideType.id}`} sx={styles.tagButton(activeType.id === slideType.id)} onClick={() => setActiveType({
            id: slideType.id,
            description: slideType.description
          })} className={`keen-slider__slide number-slide${index}"`} data-testid={`categoryCollectionTag-${slideType.id}`}>
                  <span>{slideType.name}</span>
                </button>)}
            </div>
          </div>}
      </div>
      <div sx={styles.container}>
        <div ref={ref} sx={styles.sliderContainer} className="keen-slider">
          {activeSlides.map((slide, index) => <Link key={slide.id} sx={styles.slideBox} href={slide.link ? prepareURLByCMSLink(slide.link) : '#'} className={`keen-slider__slide number-slide${index}"`} data-testid={`category-collection-carousel-block-slide-${slide.id}`}>
              {RenderMedia(slide.image, slide.video, true, slide.imagesWithBlur, slide.title, isFirst)}
              <div sx={styles.slideContent}>
                <Heading h={3} headingType={3} color={slide.titleColor || theme.rawColors.neutralWhite} mobile>
                  {slide.title}
                </Heading>
                <Icon icon="ArrowForward" size={['24px', '24px', '32px', '40px']} color={slide.titleColor || theme.rawColors.neutralWhite} />
              </div>
            </Link>)}
        </div>
        {shouldRenderArrows.right && <SlideControl direction="next" onClick={() => {
        arrows.current = true;
        slider.current?.next();
      }} />}
        {shouldRenderArrows.left && <SlideControl direction="prev" onClick={() => {
        arrows.current = true;
        slider.current?.prev();
      }} />}
        <div sx={styles.scrollbar}>
          <div style={{
          height: 1
        }} ref={scrollContainer} />
        </div>
      </div>
    </section>;
};
export default CategoryCollectionCarouselBlock;